/**
 * Call-time scope enforcement for tool files that do not go through
 * `wrapHandler`.
 *
 * WHY THIS EXISTS
 * ---------------
 * `tool-list-scope-filter.ts` narrows the ADVERTISED catalogue to what a
 * session's OAuth scopes allow, and its own header says plainly that hiding a
 * tool is not a closed door — `wrapHandler` "stays the authority" for
 * call-time enforcement.
 *
 * `stripe-connect-tools.ts` and `wallet-tools.ts` never enter `wrapHandler`.
 * Neither file contained a single `props.scope` check, so every tool in them was
 * callable by any authenticated session regardless of scope — including ones the
 * catalogue had hidden, since a client may name a tool the list never showed it.
 *
 * That reached money and other tenants' data:
 *   - `radar_review_approve` / `_decline` / `radar_value_list_add` are declared
 *     `admin` and were callable on a read-only session.
 *   - `stripe_connect_refund` (`write`) issues refunds.
 *   - `wallet_auto_topup` (`write`) charges a card off-session.
 *
 * Both files already declared the right answer — `STRIPE_CONNECT_TOOL_SCOPES`
 * and `WALLET_TOOL_SCOPES` — and used it only to build the list filter. This
 * enforces the table that was already there, so it cannot refuse a caller the
 * documented contract allowed; it only refuses ones it always said no to.
 *
 * FAILS CLOSED ON AN UNKNOWN TOOL
 * -------------------------------
 * A tool absent from the table is refused rather than allowed. A new tool added
 * without a scope entry is a mistake, and the safe reading of a missing entry is
 * "nobody", not "everybody" — the opposite default is how this hole appeared.
 */

import type { CarrierProps, ToolScope } from "./types.js";

export interface ScopeDenied {
  isError: true;
  content: Array<{ type: "text"; text: string }>;
  /**
   * The MCP SDK's `registerTool` return type carries an index signature, and a
   * closed interface stops satisfying it once the SDK infers a handler's return
   * type against the registration config. Present so a refusal can be returned
   * from any registration site without a cast.
   */
  [k: string]: unknown;
}

/**
 * Returns a denial result when the session lacks the tool's declared scope, or
 * `null` when the call may proceed.
 *
 * The message names the required scope and what the token actually holds,
 * matching `wrapUiAgentHandler`'s wording — a caller who cannot tell why they
 * were refused opens a support ticket instead of fixing their token.
 */
export function denyUnlessScoped(
  props: CarrierProps,
  toolName: string,
  scopes: Record<string, ToolScope>,
): ScopeDenied | null {
  const required = scopes[toolName];
  if (required === undefined) {
    return {
      isError: true,
      content: [
        {
          type: "text" as const,
          text:
            `Scope denied: tool '${toolName}' declares no scope, so it cannot ` +
            `be authorised. This is a server-side omission — add it to the ` +
            `tool's scope table.`,
        },
      ],
    };
  }

  const held = props.scope ?? [];
  if (held.includes(required)) return null;

  return {
    isError: true,
    content: [
      {
        type: "text" as const,
        text:
          `Scope denied: tool '${toolName}' requires '${required}' scope. ` +
          `Your token has: [${held.join(", ")}].`,
      },
    ],
  };
}
